import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { compact, find, findIndex } from "lodash/fp";

import { defaultOnSuccess } from "@app/analytics/hooks/settings/useUserSettings";
import {
  getEmailPreferences,
  updateEmailPreferences,
  EmailPreference,
  EmailPreferences,
  EmailPreferenceCategory,
} from "@app/analytics/preferences/api";
import { EmailDigest } from "@app/analytics/types";
import { showToast } from "@app/common/hooks/useToasts";
import { User } from "@app/common/types";
import { replaceAt } from "@app/utils/arrays";

// TODO: Add debounce functionality
const updateEmailPreferencesRequest = (
  newPreference: EmailPreferenceCategory
) => {
  return updateEmailPreferences({
    data: newPreference.groupStates,
  });
};

type ProductPreferenceGroup =
  | "Product Updates"
  | "Account Info"
  | "Recommendation Notifications";

export const useEmailPreferences = (user: User | null) => {
  const queryClient = useQueryClient();
  const queryKey = ["email-preferences", user?.id];
  const { data, isLoading: emailPreferencesLoading } =
    useQuery<EmailPreferences>({
      queryKey,
      queryFn: getEmailPreferences,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const emailPreferences: EmailPreference[] =
    find({ category: "meeting_recaps" }, data?.preferences)?.groupStates ?? [];

  const productPreferences: EmailPreference[] =
    find({ category: "uncategorized" }, data?.preferences)?.groupStates ?? [];

  const updateEmailPreferencesMutation = useMutation({
    mutationFn: updateEmailPreferencesRequest,
    onMutate: async (newPreferenceCategory: EmailPreferenceCategory) => {
      await queryClient.cancelQueries({ queryKey });
      const prevPreferenceResponse: EmailPreferences = queryClient.getQueryData(
        queryKey
      ) ?? { preferences: [] };

      let newEmailPreferences: EmailPreferences;

      const emailPreferenceCategoryIndex =
        prevPreferenceResponse.preferences.findIndex(
          (prefCategory) =>
            prefCategory.category === newPreferenceCategory.category
        );

      if (emailPreferenceCategoryIndex > -1) {
        newEmailPreferences = {
          preferences: replaceAt(
            prevPreferenceResponse.preferences,
            newPreferenceCategory,
            emailPreferenceCategoryIndex
          ),
        };
      } else {
        newEmailPreferences = {
          preferences: [
            ...prevPreferenceResponse.preferences,
            newPreferenceCategory,
          ],
        };
      }
      queryClient.setQueryData(queryKey, newEmailPreferences);
    },
    onError: () => {
      showToast({
        severity: "error",
        message:
          "Email preferences update has failed, please try again or refresh the page.",
      });
    },
    onSuccess: defaultOnSuccess,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const emailSubscriptions = compact(
    emailPreferences.map((pref) => (pref.isSubscribed ? pref.groupName : null))
  );

  const productSubscriptions = compact(
    productPreferences.map((pref) =>
      pref.isSubscribed ? pref.groupName : null
    )
  );

  const handleEmailPreferenceUpdate = (
    groupName: EmailDigest,
    isSubscribed: boolean
  ) => {
    const updatedPrefs = emailPreferences.map((pref) => ({
      ...pref,
      isSubscribed:
        groupName === pref.groupName ? isSubscribed : pref.isSubscribed,
    }));

    const newPrefCategory: EmailPreferenceCategory = {
      category: "meeting_recaps",
      groupStates: updatedPrefs,
    };

    updateEmailPreferencesMutation.mutate(newPrefCategory);
  };

  const handleProductPreferenceUpdate = (
    group: ProductPreferenceGroup,
    checked: boolean
  ) => {
    const prefToUpdateIndex = findIndex(
      { groupName: group },
      productPreferences
    );
    if (prefToUpdateIndex < 0) {
      throw new Error(`Product preference group "${group}" not found`);
    }

    const prefToUpdate = productPreferences[prefToUpdateIndex];

    const updatedGroupStates = replaceAt(
      productPreferences,
      { ...prefToUpdate, isSubscribed: checked },
      prefToUpdateIndex
    );

    const newPrefCategory: EmailPreferenceCategory = {
      category: "uncategorized",
      groupStates: updatedGroupStates,
    };

    updateEmailPreferencesMutation.mutate(newPrefCategory);
  };

  return {
    emailSubscriptions,
    emailPreferencesLoading,
    productSubscriptions,
    handleEmailPreferenceUpdate,
    handleProductPreferenceUpdate,
  };
};

export default useEmailPreferences;
